/**
 * x-ms-on-select side-effects helper.
 *
 * Manifest extension that lets a dynamic-list parameter (e.g. a combobox)
 * fan out its selection into sibling parameters in one declarative step:
 *
 *   'x-ms-on-select': {
 *     '<targetParameterKey>': '<sourceDataPath>'
 *   }
 *
 * On selection we look up the chosen DynamicOption, pull its `data` payload
 * (populated by the resolver), then for each entry copy
 * `data[sourceDataPath]` into the named target parameter.
 *
 * The mapping is intentionally simple: dot-paths into the `data` object,
 * no transforms. If a richer transform language is ever needed it can be
 * layered on without breaking existing manifests.
 */

import type { ValueSegment } from '@/types/valueSegment';
import { generateSegmentId, ValueSegmentType } from '@/types/valueSegment';

/** Manifest mapping shape: target parameter key → dot-path into option data. */
export type OnSelectMapping = Record<string, string>;

/** Extract `x-ms-on-select` from a parameter schema, if present and well-formed. */
export function getOnSelectMapping(
  schema: Record<string, unknown> | undefined
): OnSelectMapping | undefined {
  if (!schema) return undefined;
  const raw = schema['x-ms-on-select'];
  if (!raw || typeof raw !== 'object') return undefined;
  const entries = Object.entries(raw as Record<string, unknown>).filter(
    ([, v]) => typeof v === 'string'
  ) as [string, string][];
  if (entries.length === 0) return undefined;
  return Object.fromEntries(entries);
}

function readDotPath(source: Record<string, unknown> | undefined, path: string): unknown {
  if (!source) return undefined;
  if (!path.includes('.')) return source[path];
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, source);
}

function toLiteralSegments(value: unknown): ValueSegment[] {
  const literal =
    value == null
      ? ''
      : typeof value === 'string'
        ? value
        : typeof value === 'number' || typeof value === 'boolean'
          ? String(value)
          : JSON.stringify(value);
  return [
    {
      id: generateSegmentId(),
      type: ValueSegmentType.LITERAL,
      value: literal,
    },
  ];
}

/**
 * Apply an on-select mapping using the chosen option's `data` payload.
 *
 * @param mapping Result of `getOnSelectMapping(parameterInfo.schema)`
 * @param optionData The `data` field of the selected DynamicOption
 * @param setParameter Node-editor store setter; ValueSegment[] values are
 *                    written so they participate in tokenization downstream
 */
export function applyOnSelectMapping(
  mapping: OnSelectMapping,
  optionData: Record<string, unknown> | undefined,
  setParameter: (key: string, value: ValueSegment[]) => void
): void {
  for (const [targetKey, sourcePath] of Object.entries(mapping)) {
    const value = readDotPath(optionData, sourcePath);
    if (value === undefined) continue;
    setParameter(targetKey, toLiteralSegments(value));
  }
}
